'use client';

import React, { useState, useRef } from 'react';
import { Button } from '@/components/common/Button';
import { Sparkles, Crop, Undo2, Maximize2, Minimize2, Check } from 'lucide-react';
import ReactCrop, { Crop as CropType, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropPreviewProps {
  imageData: string;
  mimeType: string;
  size: number;
  height?: number; // Total component height in pixels for normal view (default: 400)
  onApplyCrop: (croppedImageData: string, croppedSize: number) => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

// Aspect ratio presets for quick cropping
const cropAspectRatios = [
  { value: 21 / 9, label: '21:9', description: 'Ultra-wide' },
  { value: 3 / 1, label: '3:1', description: 'Wide banner' },
  { value: 2 / 1, label: '2:1', description: 'Banner' },
  { value: 16 / 9, label: '16:9', description: 'Widescreen' },
  { value: 4 / 3, label: '4:3', description: 'Standard' },
  { value: 1, label: '1:1', description: 'Square' },
  { value: 9 / 16, label: '9:16', description: 'Portrait' },
];

export const ImageCropPreview: React.FC<ImageCropPreviewProps> = ({
  imageData,
  mimeType,
  size,
  height = 400,
  onApplyCrop,
  onFullscreenChange,
}) => {
  const [crop, setCrop] = useState<CropType>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isCropping, setIsCropping] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lockedImageSize, setLockedImageSize] = useState<{ width: number; height: number } | null>(null);
  const [currentAspectRatio, setCurrentAspectRatio] = useState<number | undefined>();
  const [isDraggingCorner, setIsDraggingCorner] = useState(false);
  const [dragStartCrop, setDragStartCrop] = useState<CropType | undefined>();
  const [originalImageData, setOriginalImageData] = useState<string>(imageData);
  const [currentImageData, setCurrentImageData] = useState<string>(imageData);
  const [currentSize, setCurrentSize] = useState<number>(size);
  const [hasCropped, setHasCropped] = useState(false);
  const [savedCrop, setSavedCrop] = useState<CropType | undefined>();
  const [savedCompletedCrop, setSavedCompletedCrop] = useState<PixelCrop | undefined>();
  // Store crop in percentage coordinates (relative to image, not display size)
  const [cropInPercent, setCropInPercent] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);

  // Update locked image size and recalculate crop when fullscreen changes
  React.useEffect(() => {
    if (isCropping && imgRef.current && lockedImageSize) {
      // Step 1: Clear crop immediately to avoid mask layer glitch
      if (cropInPercent) {
        setCrop(undefined);
      }
      
      // Step 2: Recalculate size and recreate crop after transition
      const timeoutId = setTimeout(() => {
        if (imgRef.current && cropContainerRef.current && cropInPercent) {
          // Get container dimensions
          const containerWidth = cropContainerRef.current.clientWidth - 32; // minus padding
          const containerHeight = cropContainerRef.current.clientHeight - 32;
          
          // Calculate scaled dimensions maintaining aspect ratio
          const imgAspect = lockedImageSize.width / lockedImageSize.height;
          let newWidth, newHeight;
          
          if (containerWidth / containerHeight > imgAspect) {
            // Container is wider - fit to height
            newHeight = containerHeight;
            newWidth = newHeight * imgAspect;
          } else {
            // Container is taller - fit to width
            newWidth = containerWidth;
            newHeight = newWidth / imgAspect;
          }
          
          // Update image size
          setLockedImageSize({ width: newWidth, height: newHeight });
          
          // Step 3: Recreate crop with saved percentages
          const newCrop: CropType = {
            unit: '%',
            x: cropInPercent.x,
            y: cropInPercent.y,
            width: cropInPercent.width,
            height: cropInPercent.height,
          };
          setCrop(newCrop);
        }
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen]);

  // Quick crop with aspect ratio - sets initial crop area
  const handleQuickCrop = (aspectValue: number) => {
    // Store original if first time cropping
    if (!hasCropped) {
      setOriginalImageData(currentImageData);
    }
    
    if (imgRef.current) {
      // Lock the current displayed dimensions
      const imgWidth = imgRef.current.clientWidth;
      const imgHeight = imgRef.current.clientHeight;
      setLockedImageSize({ width: imgWidth, height: imgHeight });
      
      const imgAspect = imgWidth / imgHeight;

      let cropWidth, cropHeight, x, y;

      if (aspectValue > imgAspect) {
        // Crop is wider than image - fit width
        cropWidth = 100;
        cropHeight = (imgAspect / aspectValue) * 100;
        x = 0;
        y = (100 - cropHeight) / 2;
      } else {
        // Crop is taller than image - fit height
        cropHeight = 100;
        cropWidth = (aspectValue / imgAspect) * 100;
        x = (100 - cropWidth) / 2;
        y = 0;
      }

      const initialCrop = {
        unit: '%' as const,
        width: cropWidth,
        height: cropHeight,
        x,
        y
      };
      
      setCrop(initialCrop);
      // Save crop in percentages for zoom/unzoom
      setCropInPercent({ x, y, width: cropWidth, height: cropHeight });
      setCurrentAspectRatio(cropWidth / cropHeight); // Set actual aspect ratio
      setIsCropping(true); // Enter cropping mode after crop is set
    }
  };

  // Custom onChange handler that applies aspect ratio only for corner drags
  const handleCropChange = (newCrop: CropType, percentCrop: CropType) => {
    // Always save the crop in percentages for zoom/unzoom
    setCropInPercent({
      x: percentCrop.x || 0,
      y: percentCrop.y || 0,
      width: percentCrop.width || 0,
      height: percentCrop.height || 0,
    });
    
    if (!dragStartCrop || !currentAspectRatio) {
      setCrop(newCrop);
      return;
    }

    // CRITICAL: ALWAYS use percentCrop for comparison
    // dragStartCrop might be in pixels OR percentages depending on when it was stored
    // But percentCrop parameter is ALWAYS in percentages
    // So we need to convert dragStartCrop to percentages if it's in pixels
    let dragStartInPercent = dragStartCrop;
    if (dragStartCrop.unit === 'px' && imgRef.current) {
      // Convert pixel crop to percentage
      const imgWidth = imgRef.current.clientWidth;
      const imgHeight = imgRef.current.clientHeight;
      dragStartInPercent = {
        unit: '%' as const,
        x: ((dragStartCrop.x || 0) / imgWidth) * 100,
        y: ((dragStartCrop.y || 0) / imgHeight) * 100,
        width: ((dragStartCrop.width || 0) / imgWidth) * 100,
        height: ((dragStartCrop.height || 0) / imgHeight) * 100,
      };
    }
    
    // Check if this is a corner drag by comparing both width and height changes
    const widthChanged = Math.abs((dragStartInPercent.width || 0) - (percentCrop.width || 0)) > 0.1;
    const heightChanged = Math.abs((dragStartInPercent.height || 0) - (percentCrop.height || 0)) > 0.1;
    const isCornerDrag = widthChanged && heightChanged;

    if (isCornerDrag) {
      // Corner drag - maintain aspect ratio
      const newWidth = newCrop.width || 0;
      const newHeight = newWidth / currentAspectRatio;
      
      setCrop({
        ...newCrop,
        height: newHeight
      });
    } else {
      // Edge drag - free resize
      // DON'T update aspect ratio here - only update on drag end
      setCrop(newCrop);
    }
  };

  // Handle drag start - store initial crop
  const handleDragStart = () => {
    setDragStartCrop(crop);
  };

  // Handle drag end - clear drag state
  const handleDragEnd = () => {
    setDragStartCrop(undefined);
    // Update aspect ratio based on final crop
    if (crop && crop.width && crop.height) {
      setCurrentAspectRatio(crop.width / crop.height);
    }
  };

  const handlePreviewCrop = async () => {
    // Preview the crop without permanently applying it
    if (!cropInPercent || !imgRef.current) return;

    const canvas = document.createElement('canvas');
    const image = imgRef.current;
    
    // Work directly with the ORIGINAL (natural) image dimensions
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    // Convert percentage crop to pixel coordinates on the ORIGINAL image
    const cropX = (cropInPercent.x / 100) * naturalWidth;
    const cropY = (cropInPercent.y / 100) * naturalHeight;
    const cropWidth = (cropInPercent.width / 100) * naturalWidth;
    const cropHeight = (cropInPercent.height / 100) * naturalHeight;

    // Set canvas to the cropped size (from original image)
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Draw the cropped portion from the ORIGINAL image
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    // Convert canvas to base64
    canvas.toBlob((blob) => {
      if (!blob) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        const base64String = base64data.split(',')[1];
        
        setCurrentImageData(base64String);
        setCurrentSize(blob.size);
        setHasCropped(true);
        setIsCropping(false);
        setLockedImageSize(null); // Clear locked size
        
        // Save crop state for potential revert
        setSavedCrop(crop);
        setSavedCompletedCrop(completedCrop);
        
        // Notify parent with cropped data (preview only)
        onApplyCrop(base64String, blob.size);
      };
      reader.readAsDataURL(blob);
    }, mimeType);
  };

  const handleSaveAndApply = async () => {
    // Permanently apply the crop - the cropped image becomes the new original
    if (!cropInPercent) return;

    // Create a temporary image element to load the ORIGINAL image
    const originalImage = new Image();
    originalImage.src = `data:${mimeType};base64,${originalImageData}`;
    
    originalImage.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Work directly with the ORIGINAL (natural) image dimensions
      const naturalWidth = originalImage.naturalWidth;
      const naturalHeight = originalImage.naturalHeight;

      // Convert percentage crop to pixel coordinates on the ORIGINAL image
      const cropX = (cropInPercent.x / 100) * naturalWidth;
      const cropY = (cropInPercent.y / 100) * naturalHeight;
      const cropWidth = (cropInPercent.width / 100) * naturalWidth;
      const cropHeight = (cropInPercent.height / 100) * naturalHeight;

      // Set canvas to the cropped size (from original image)
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      // Draw the cropped portion from the ORIGINAL image
      ctx.drawImage(
        originalImage,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      // Convert canvas to base64
      canvas.toBlob((blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          const base64String = base64data.split(',')[1];
          
          // PERMANENTLY replace the original image with the cropped version
          setOriginalImageData(base64String);
          setCurrentImageData(base64String);
          setCurrentSize(blob.size);
          setHasCropped(false); // No longer in cropped state - this IS the image now
          setIsCropping(false);
          setLockedImageSize(null);
          setCropInPercent(null); // Clear crop data
          setSavedCrop(undefined);
          setSavedCompletedCrop(undefined);
          
          // Notify parent with the new permanent image
          onApplyCrop(base64String, blob.size);
        };
        reader.readAsDataURL(blob);
      }, mimeType);
    };
  };

  const handleCancelCrop = () => {
    setIsCropping(false);
    setLockedImageSize(null); // Clear locked size
    setCrop(savedCrop); // Restore saved crop if any
  };

  const handleRevertCrop = () => {
    // Revert to original image and restore crop state for editing
    setCurrentImageData(originalImageData);
    setCurrentSize(size);
    setHasCropped(false);
    setIsCropping(true); // Enter cropping mode
    
    // Restore saved crop state
    if (savedCrop) {
      setCrop(savedCrop);
    }
    if (savedCompletedCrop) {
      setCompletedCrop(savedCompletedCrop);
    }
    
    // Notify parent with original data
    onApplyCrop(originalImageData, size);
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-8' : ''}`}>
      <div 
        className={`border-2 border-orange-300 rounded-lg overflow-hidden bg-orange-50 ${isFullscreen ? 'w-full h-full flex flex-col' : 'flex flex-col'}`}
        style={!isFullscreen ? { height: `${height}px` } : undefined}
      >
        {/* Header with title and action buttons */}
        <div className="bg-orange-100 px-4 py-2 border-b border-orange-200 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-semibold text-orange-900">
                Preview - Not Yet Saved
                {hasCropped && !isCropping && <span className="ml-2 text-orange-600">(Cropped)</span>}
              </span>
              <span className="text-xs text-gray-600 ml-2">
                • {(currentSize / 1024).toFixed(2)} KB
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Action buttons based on state */}
              {isCropping ? (
                // Cropping mode: Preview & Cancel
                <>
                  <Button
                    onClick={handleCancelCrop}
                    variant="outline"
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePreviewCrop}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!crop}
                  >
                    Preview
                  </Button>
                </>
              ) : hasCropped ? (
                // Cropped preview mode: Revert & Save buttons
                <>
                  <Button
                    onClick={handleRevertCrop}
                    variant="outline"
                    size="sm"
                    title="Revert to original and edit crop"
                  >
                    <Undo2 className="w-3 h-3 mr-1" />
                    Revert
                  </Button>
                  <Button
                    onClick={handleSaveAndApply}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    title="Permanently apply crop (cannot undo)"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Save & Apply
                  </Button>
                </>
              ) : null}
              
              {/* Fullscreen toggle button - rightmost */}
              <button
                onClick={() => {
                  const newState = !isFullscreen;
                  console.log('🖱️ Fullscreen toggle clicked:', {
                    from: isFullscreen,
                    to: newState,
                    isCropping,
                    currentLockedSize: lockedImageSize
                  });
                  setIsFullscreen(newState);
                  onFullscreenChange?.(newState);
                }}
                className="p-1 hover:bg-orange-200 rounded transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen view'}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4 text-orange-600" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-orange-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Crop ratio buttons - always visible when not cropping */}
        {!isCropping && (
          <div className="bg-orange-50 px-4 py-2 border-b border-orange-200">
            <div className="flex items-center gap-2">
              <Crop className="w-3 h-3 text-orange-600 flex-shrink-0" />
              <span className="text-xs text-orange-700 font-medium">Quick Crop:</span>
              <div className="flex flex-wrap gap-1">
                {cropAspectRatios.map((ratio) => (
                  <button
                    key={ratio.label}
                    onClick={() => handleQuickCrop(ratio.value)}
                    title={ratio.description}
                    className="px-2 py-0.5 rounded text-xs font-medium bg-white border border-orange-300 text-orange-800 hover:bg-orange-50 transition-colors"
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div 
          className="flex-1 min-h-0 flex items-center justify-center p-4"
          ref={cropContainerRef}
        >
          {isCropping ? (
            <ReactCrop
              crop={crop}
              onChange={handleCropChange}
              onComplete={(c) => setCompletedCrop(c)}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              keepSelection
            >
              <img
                ref={imgRef}
                src={`data:${mimeType};base64,${currentImageData}`}
                alt="Generated preview"
                className="rounded"
                style={lockedImageSize ? { 
                  width: `${lockedImageSize.width}px`,
                  height: `${lockedImageSize.height}px`,
                  display: 'block'
                } : {
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </ReactCrop>
          ) : (
            <img
              ref={imgRef}
              src={`data:${mimeType};base64,${currentImageData}`}
              alt="Generated preview"
              className="rounded max-w-full max-h-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
};
